import { useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import Logo from '../../components/Logo.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import ClientTargetField from '../../components/ClientTargetField.jsx'
import { defaultTarget, targetClients, targetError, targetPayload } from '../../lib/clientTarget.js'

/**
 * Picker pós-OAuth (etapa de confirmação, página focada / sem sidebar).
 *
 * Recebe via location.state a lista de contas Google Ads autorizadas
 * (connectionId + customers, vindos do callback). O cliente é o dono das contas
 * (#351): o usuário escolhe, por conta, um cliente existente ou um novo; várias
 * contas podem ir para o mesmo cliente. Conta já vinculada fica no cliente atual.
 *
 * Nada é persistido até o "Salvar": ali vinculamos as ad_accounts ao cliente
 * escolhido (criando-o se for novo) de uma vez (atômico no backend).
 *
 * Se o user veio direto pra URL (refresh, deep link), redireciona ao perfil.
 */
export default function GoogleAdsAccountPicker() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast, refreshAccess, availableClients, activeOrganizationId, selectedClient } = useApp()
  const data = location.state

  if (!data?.connectionId || !Array.isArray(data?.customers)) {
    return <Navigate to="/perfil" replace />
  }

  const clients = useMemo(
    () => targetClients(availableClients, activeOrganizationId),
    [availableClients, activeOrganizationId]
  )

  // Estado por conta: { checked, client, newName, domain }. O usuário escolhe quais contas vincular:
  // entram marcadas só as já vinculadas (reconexão: atualiza a conexão delas); o resto, desmarcado.
  const [rows, setRows] = useState(() => {
    const initial = {}
    for (const c of data.customers) {
      initial[c.customer_id] = {
        checked: !!c.linked_client_id,
        client: defaultTarget(c.linked_client_id, clients, selectedClient?.id),
        newName: c.descriptive_name || '',
        domain: '',
      }
    }
    return initial
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const toggle = (customerId) =>
    setRows((prev) => ({
      ...prev,
      [customerId]: { ...prev[customerId], checked: !prev[customerId].checked },
    }))

  const patchRow = (customerId, patch) =>
    setRows((prev) => ({
      ...prev,
      [customerId]: { ...prev[customerId], ...patch },
    }))

  const accountNameOf = (c) => c.descriptive_name || `Conta ${c.customer_id}`

  const canSubmit = useMemo(() => {
    const checked = data.customers.filter((c) => rows[c.customer_id]?.checked)
    return (
      checked.length > 0 &&
      checked.every((c) => !targetError(rows[c.customer_id], clients))
    )
  }, [data.customers, rows, clients])

  async function handleSubmit() {
    setError('')
    const checked = data.customers.filter((c) => rows[c.customer_id]?.checked)

    if (checked.length === 0) {
      setError('Selecione pelo menos uma conta para vincular.')
      return
    }
    const invalid = checked.map((c) => targetError(rows[c.customer_id], clients)).find(Boolean)
    if (invalid) {
      setError(invalid)
      return
    }

    let accounts
    try {
      accounts = checked.map((c) => ({
        customer_id: c.customer_id,
        account_name: accountNameOf(c),
        ...targetPayload(rows[c.customer_id], clients),
        is_manager: !!c.is_manager,
        // login_customer_id SEMPRE null aqui. Toda conta desta tela veio de
        // ListAccessibleCustomers, ou seja, é DIRETAMENTE acessível pelo token
        // OAuth do usuário (coleta via Tier 1 direto). Carimbar o MCC como
        // login-customer-id quebrava a coleta com USER_PERMISSION_DENIED quando
        // o acesso à conta não é, de fato, através daquele manager. Uma
        // sub-conta que só fosse alcançável via manager sequer apareceria nesta
        // lista — então o header de manager nunca é necessário neste fluxo.
        login_customer_id: null,
      }))
    } catch (err) {
      setError(err.message)
      return
    }

    try {
      setSubmitting(true)
      const res = await api.linkGoogleAdsAccounts({
        connectionId: data.connectionId,
        accounts,
      })
      await refreshAccess?.()
      toast(
        `${res.ad_account_ids.length} conta(s) Google Ads vinculadas com sucesso.`,
        'success'
      )
      navigate(data.returnTo || '/google-ads', { replace: true })
    } catch (err) {
      setError(err.message || 'Erro ao vincular contas.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="ob-shell">
      <div className="ob-topbar">
        <div className="ob-logo"><Logo height={16} /></div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate(data.returnTo || '/perfil')}
          disabled={submitting}
        >
          Cancelar
        </button>
      </div>

      <div className="ob-body">
        <div className="ob-card" style={{ maxWidth: 640, width: '100%' }}>
          <div className="ob-badge">Passo 2 de 2</div>
          <h1 className="ob-title">Confirme suas contas Google Ads</h1>
          <p className="ob-desc">
            Escolha a qual cliente cada conta pertence. Várias contas podem ficar
            no mesmo cliente. Salve para concluir.
            <br />
            <small style={{ color: 'var(--ter)' }}>Conectado como {data.googleEmail}</small>
          </p>

          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}

          {data.customers.length === 0 ? (
            <div className="empty">
              <div className="empty-desc">
                A conta {data.googleEmail} não tem nenhum customer_id acessível
                no Google Ads. Verifique permissões e tente novamente.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {data.customers.map((c) => {
                const row = rows[c.customer_id] || {}
                return (
                  <div
                    key={c.customer_id}
                    className="section-card"
                    style={{
                      padding: 16,
                      opacity: row.checked ? 1 : 0.55,
                      transition: 'opacity .15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!row.checked}
                        onChange={() => toggle(c.customer_id)}
                        style={{ width: 16, height: 16, marginTop: 4 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="conn-name">
                          {accountNameOf(c)}
                          {c.is_manager && (
                            <span className="badge badge-amber" style={{ marginLeft: 8 }}>MCC</span>
                          )}
                          {c.is_test_account && (
                            <span className="badge" style={{ marginLeft: 8 }}>Teste</span>
                          )}
                        </div>
                        <div className="conn-meta" style={{ marginBottom: 10 }}>
                          Customer ID: {formatCustomerId(c.customer_id)}
                          {c.currency_code ? ` · ${c.currency_code}` : ''}
                          {c.time_zone ? ` · ${c.time_zone}` : ''}
                        </div>
                        <ClientTargetField
                          row={row}
                          clients={clients}
                          linkedClientId={c.linked_client_id}
                          disabled={!row.checked}
                          onChange={(patch) => patchRow(c.customer_id, patch)}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <button
            className="btn btn-black btn-full"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            style={{ marginTop: 20 }}
          >
            {submitting ? 'Salvando…' : 'Salvar e concluir'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Formata 10 dígitos como 123-456-7890 (padrão Google Ads). Aceita já formatado.
function formatCustomerId(id) {
  const digits = String(id || '').replace(/\D/g, '')
  if (digits.length !== 10) return id
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}
